import { Disc3, Camera, Sparkles } from "lucide-react";
import RecordPriceCheck from "./RecordPriceCheck";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const RECORD_GRADES = ["Mint (M)", "Near Mint (NM or M-)", "Very Good Plus (VG+)", "Very Good (VG)", "Good Plus (G+)", "Good (G)", "Fair (F)", "Poor (P)"];
const SLEEVE_GRADES = [...RECORD_GRADES, "Generic", "No Cover"];

function GradeSelect({ label, value, options, onChange, testid }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger className="h-9 text-xs" data-testid={testid}><SelectValue placeholder="Pick grade" /></SelectTrigger>
        <SelectContent>
          {options.map((g) => <SelectItem key={g} value={g} data-testid={`${testid}-${g.replace(/[^a-z+]/gi, "").toLowerCase()}`}>{g}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

const PRESS = {
  first: { label: "Possible first press", cls: "border-violet-300 bg-violet-50 text-violet-900" },
  early: { label: "Possible early pressing", cls: "border-violet-200 bg-violet-50 text-violet-900" },
  reissue: { label: "Looks like a reissue", cls: "border-slate-200 bg-white text-slate-700" },
  unknown: { label: "Pressing unclear", cls: "border-slate-200 bg-white text-slate-700" },
};

function PressingFlag({ p }) {
  if (!p?.likely) return null;
  const meta = PRESS[p.likely];
  return (
    <div className={`mt-2 rounded-md border p-2 ${meta.cls}`} data-testid="record-pressing-flag" data-likely={p.likely}>
      <p className="flex items-center gap-1 text-[11px] font-semibold"><Sparkles className="h-3 w-3" /> <span data-testid="record-pressing-label">{meta.label}</span></p>
      {p.clues?.length > 0 && <p className="mt-0.5 text-[11px]" data-testid="record-pressing-clues">Clues: {p.clues.join("; ")}</p>}
      {p.check?.length > 0 && (
        <ul className="mt-0.5 list-disc pl-4 text-[11px]" data-testid="record-pressing-checks">
          {p.check.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      )}
    </div>
  );
}

export default function RecordModeCard({ draft, patch }) {
  const rec = draft?.record;
  if (!rec?.media_type) return null;
  const set = (k) => (v) => patch({ record: { ...rec, [k]: v } });
  return (
    <div className="rounded-lg border border-slate-900/10 bg-slate-50 p-3" data-testid="record-mode-card">
      <div className="mb-2 flex items-center gap-2">
        <Disc3 className="h-4 w-4 text-slate-900" />
        <span className="text-sm font-semibold text-slate-900">Record mode</span>
        <span className="rounded-full bg-slate-900 px-2 py-0.5 font-mono text-[10px] text-white" data-testid="record-media-type">{rec.media_type}</span>
      </div>
      <div className="grid grid-cols-1 gap-2 min-[440px]:grid-cols-2">
        <GradeSelect label="Media grade" value={rec.media_grade} options={RECORD_GRADES} onChange={set("media_grade")} testid="record-media-grade" />
        <GradeSelect label="Sleeve grade" value={rec.sleeve_grade} options={SLEEVE_GRADES} onChange={set("sleeve_grade")} testid="record-sleeve-grade" />
      </div>
      {rec.grade_notes && <p className="mt-2 text-[11px] leading-relaxed text-slate-600" data-testid="record-grade-notes">{rec.grade_notes}</p>}
      <PressingFlag p={rec.pressing} />
      <RecordPriceCheck draft={draft} patch={patch} />
      {rec.missing_photos?.length > 0 && (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2" data-testid="record-photo-tips">
          <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-amber-900"><Camera className="h-3 w-3" /> Add these photos for a stronger listing</p>
          <ul className="list-disc space-y-0.5 pl-4 text-[11px] text-amber-900">
            {rec.missing_photos.map((t, i) => <li key={i} data-testid={`record-photo-tip-${i}`}>{t}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
